<template>
  <a-modal
    v-model:open="visible"
    :title="title"
    :width="width"
    :confirm-loading="loading"
    :mask-closable="maskClosable"
    :keyboard="keyboard"
    :destroy-on-close="destroyOnClose"
    @ok="handleOk"
    @cancel="handleCancel"
  >
    <a-form
      ref="formRef"
      :model="model"
      :rules="rules"
      :layout="layout"
      :label-col="labelCol"
      :wrapper-col="wrapperCol"
    >
      <slot />
    </a-form>

    <template v-if="showFooter" #footer>
      <slot name="footer">
        <a-button @click="handleCancel">
          {{ resolvedCancelText }}
        </a-button>
        <a-button type="primary" :loading="loading" @click="handleOk">
          {{ resolvedOkText }}
        </a-button>
      </slot>
    </template>
  </a-modal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { FormInstance, FormProps } from 'ant-design-vue'

interface Props {
  /** 是否显示 */
  visible: boolean
  /** 标题 */
  title: string
  /** 宽度 */
  width?: string | number
  /** 加载状态 */
  loading?: boolean
  /** 表单数据模型 */
  model: Record<string, any>
  /** 表单验证规则 */
  rules?: FormProps['rules']
  /** 表单布局 */
  layout?: 'horizontal' | 'vertical' | 'inline'
  /** 标签列配置 */
  labelCol?: FormProps['labelCol']
  /** 包装列配置 */
  wrapperCol?: FormProps['wrapperCol']
  /** 确认按钮文本 */
  okText?: string
  /** 取消按钮文本 */
  cancelText?: string
  /** 是否显示底部 */
  showFooter?: boolean
  /** 点击遮罩层是否关闭 */
  maskClosable?: boolean
  /** 是否支持ESC关闭 */
  keyboard?: boolean
  /** 关闭时销毁子元素 */
  destroyOnClose?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  width: 600,
  loading: false,
  layout: 'vertical',
  showFooter: true,
  maskClosable: false,
  keyboard: true,
  destroyOnClose: true
})

const { t } = useI18n()

const resolvedOkText = computed(() => props.okText ?? t('common.ok'))
const resolvedCancelText = computed(() => props.cancelText ?? t('common.cancel'))

const emit = defineEmits<{
  'update:visible': [visible: boolean]
  submit: [values: Record<string, any>]
  cancel: []
}>()

const formRef = ref<FormInstance>()

// 内部visible状态
const visible = computed({
  get: () => props.visible,
  set: (val) => emit('update:visible', val)
})

// 监听visible变化，重置表单
watch(() => props.visible, (newVal) => {
  if (!newVal) {
    formRef.value?.resetFields()
  }
})

// 处理确认
const handleOk = async () => {
  try {
    const values = await formRef.value?.validate()
    if (values) {
      emit('submit', values)
    }
  } catch (error) {
    // 验证失败，不关闭弹窗
    console.error('表单验证失败:', error)
  }
}

// 处理取消
const handleCancel = () => {
  emit('cancel')
  visible.value = false
}

// 暴露表单实例方法
defineExpose({
  validate: () => formRef.value?.validate(),
  validateFields: (fields: string[]) => formRef.value?.validateFields(fields),
  resetFields: () => formRef.value?.resetFields(),
  getFieldsValue: () => formRef.value?.getFieldsValue(),
  setFieldsValue: (values: Record<string, any>) => formRef.value?.setFieldsValue(values)
})
</script>


<style scoped lang="less">
// 表单模态框样式
</style>

